import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { formatNumber, formatDate, statusBadgeClass, statusLabel } from '../utils/formatters';
import toast from 'react-hot-toast';
import { Search, Plus, Eye, ChevronLeft, ChevronRight, X, ArrowRight } from 'lucide-react';

function TransferForm({ warehouses, onSubmit, onClose, saving }) {
  const [form, setForm] = useState({
    source_warehouse_id: '',
    destination_warehouse_id: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [, setProductsLoading] = useState(false);

  const fetchProducts = async (search = '') => {
    setProductsLoading(true);
    try {
      const params = queryParams({ limit: 50, search });
      const res = await api.get('/products', { params });
      setProducts(res.data.data?.products || res.data.data || []);
    } catch (error) {
      handleApiError(error);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleProductSearch = (value) => {
    setProductSearch(value);
    setShowProductDropdown(true);
    if (value.length >= 2) {
      fetchProducts(value);
    }
  };

  const addProduct = (product) => {
    if (selectedProducts.find((p) => p.product_id === product.id)) {
      toast.warning('Produk sudah ditambahkan');
      return;
    }
    setSelectedProducts((prev) => [
      ...prev,
      {
        product_id: product.id,
        name: product.name,
        sku: product.sku,
        available_stock: product.stock || 0,
        quantity: 1,
      },
    ]);
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const removeProduct = (productId) => {
    setSelectedProducts((prev) => prev.filter((p) => p.product_id !== productId));
  };

  const updateQuantity = (productId, qty) => {
    setSelectedProducts((prev) =>
      prev.map((p) => (p.product_id === productId ? { ...p, quantity: Math.max(1, Number(qty) || 1) } : p))
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.source_warehouse_id || !form.destination_warehouse_id) {
      toast.error('Gudang asal dan tujuan harus dipilih');
      return;
    }
    if (form.source_warehouse_id === form.destination_warehouse_id) {
      toast.error('Gudang asal dan tujuan tidak boleh sama');
      return;
    }
    if (selectedProducts.length === 0) {
      toast.error('Tambahkan minimal satu produk');
      return;
    }
    onSubmit({
      ...form,
      items: selectedProducts.map((p) => ({ product_id: p.product_id, quantity: p.quantity })),
    });
  };

  const filteredProducts = products.filter(
    (p) => !selectedProducts.find((sp) => sp.product_id === p.id)
  );

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="label">Gudang Asal <span className="text-danger-600">*</span></label>
          <select
            className="input"
            value={form.source_warehouse_id}
            onChange={(e) => setForm({ ...form, source_warehouse_id: e.target.value })}
            required
          >
            <option value="">Pilih Gudang Asal</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Gudang Tujuan <span className="text-danger-600">*</span></label>
          <select
            className="input"
            value={form.destination_warehouse_id}
            onChange={(e) => setForm({ ...form, destination_warehouse_id: e.target.value })}
            required
          >
            <option value="">Pilih Gudang Tujuan</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Tanggal <span className="text-danger-600">*</span></label>
          <input
            type="date"
            className="input"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            required
          />
        </div>
      </div>

      <div>
        <label className="label">Catatan</label>
        <textarea
          className="input min-h-[60px]"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Catatan transfer (opsional)"
          rows={2}
        />
      </div>

      {/* Product Selection */}
      <div>
        <label className="label">Produk <span className="text-danger-600">*</span></label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            className="input pl-9"
            placeholder="Cari dan tambah produk..."
            value={productSearch}
            onChange={(e) => handleProductSearch(e.target.value)}
            onFocus={() => setShowProductDropdown(true)}
            onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
          />
          {showProductDropdown && filteredProducts.length > 0 && (
            <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {filteredProducts.slice(0, 15).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm flex items-center justify-between"
                  onMouseDown={() => addProduct(p)}
                >
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">{p.name}</span>
                    <span className="text-gray-500 dark:text-gray-400 ml-2">({p.sku})</span>
                  </div>
                  <span className="text-xs text-gray-500">Stok: {formatNumber(p.stock)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Selected Products */}
      {selectedProducts.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <table className="table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Nama Produk</th>
                <th className="text-right">Stok Tersedia</th>
                <th className="text-right">Jumlah Transfer</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {selectedProducts.map((item) => (
                <tr key={item.product_id}>
                  <td>
                    <span className="font-mono text-xs text-gray-600 dark:text-gray-400">{item.sku}</span>
                  </td>
                  <td>
                    <span className="font-medium text-gray-900 dark:text-white">{item.name}</span>
                  </td>
                  <td className="text-right">
                    <span className="text-sm text-gray-700 dark:text-gray-200">{formatNumber(item.available_stock)}</span>
                  </td>
                  <td className="text-right">
                    <input
                      type="number"
                      min="1"
                      className="input w-24 text-right"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(item.product_id, e.target.value)}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => removeProduct(item.product_id)}
                      className="p-1 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
        <button type="button" onClick={onClose} className="btn-secondary flex-1">Batal</button>
        <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Mengirim...' : 'Buat Transfer'}</button>
      </div>
    </form>
  );
}

export default function StockTransferPage() {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [viewingTransfer, setViewingTransfer] = useState(null);

  useEffect(() => {
    fetchTransfers();
  }, [pagination.page, filters]);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchTransfers = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/inventory/transfers', { params });
      if (res.data.success) {
        setTransfers(res.data.data?.transfers || res.data.data?.items || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const res = await api.get('/warehouses', { params: { limit: 1000 } });
      setWarehouses(res.data.data?.warehouses || res.data.data || []);
    } catch (error) {
      // Silently fail
    }
  };

  const handleCreate = async (data) => {
    setSaving(true);
    try {
      const res = await api.post('/inventory/transfer', data);
      if (res.data.success) {
        toast.success('Transfer stok berhasil dibuat');
        setShowCreateModal(false);
        fetchTransfers();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleViewTransfer = async (transfer) => {
    try {
      const res = await api.get(`/inventory/transfers/${transfer.id}`);
      if (res.data.success) {
        setViewingTransfer(res.data.data);
      }
    } catch (error) {
      // Fallback: show basic info
      setViewingTransfer(transfer);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Transfer Stok</h1>
          <p className="page-subtitle">Transfer stok antar gudang</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <Plus className="h-4 w-4 mr-2" /> Transfer Baru
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari nomor transfer..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchTransfers()}
              />
            </div>
            <div>
              <select
                className="input"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Semua Status</option>
                <option value="pending">Menunggu</option>
                <option value="in_progress">Berlangsung</option>
                <option value="completed">Selesai</option>
                <option value="cancelled">Dibatalkan</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={fetchTransfers} className="btn-primary btn-sm">Cari</button>
              <button onClick={() => setFilters({ search: '', status: '' })} className="btn-secondary btn-sm">Reset</button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-32" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-36" />
                  <div className="skeleton h-4 w-36" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        ) : transfers.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <ArrowRight className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data Transfer</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada transfer stok yang dilakukan.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>No. Transfer</th>
                    <th>Tanggal</th>
                    <th>Gudang Asal</th>
                    <th>Gudang Tujuan</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {transfers.map((transfer) => (
                    <tr key={transfer.id}>
                      <td>
                        <span className="font-medium text-gray-900 dark:text-white">{transfer.doc_number || transfer.number || '-'}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(transfer.date || transfer.created_at)}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{transfer.source_warehouse?.name || transfer.source_warehouse_name || '-'}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{transfer.destination_warehouse?.name || transfer.destination_warehouse_name || '-'}</span>
                      </td>
                      <td>
                        <span className={`badge ${statusBadgeClass(transfer.status)}`}>{statusLabel(transfer.status)}</span>
                      </td>
                      <td>
                        <button
                          onClick={() => handleViewTransfer(transfer)}
                          className="p-1.5 text-primary-900 hover:bg-primary-50 dark:hover:bg-primary-950/50 rounded"
                          title="Lihat Detail"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* View Transfer Detail Modal */}
      {viewingTransfer && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Detail Transfer</h3>
              <button onClick={() => setViewingTransfer(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">No. Transfer</p>
                  <p className="font-medium text-gray-900 dark:text-white">{viewingTransfer.doc_number || viewingTransfer.number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Tanggal</p>
                  <p className="font-medium text-gray-900 dark:text-white">{formatDate(viewingTransfer.date || viewingTransfer.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Gudang Asal</p>
                  <p className="font-medium text-gray-900 dark:text-white">{viewingTransfer.source_warehouse?.name || viewingTransfer.source_warehouse_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Gudang Tujuan</p>
                  <p className="font-medium text-gray-900 dark:text-white">{viewingTransfer.destination_warehouse?.name || viewingTransfer.destination_warehouse_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                  <span className={`badge ${statusBadgeClass(viewingTransfer.status)}`}>{statusLabel(viewingTransfer.status)}</span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Catatan</p>
                  <p className="text-sm text-gray-700 dark:text-gray-200">{viewingTransfer.notes || '-'}</p>
                </div>
              </div>
              {viewingTransfer.items && viewingTransfer.items.length > 0 && (
                <div>
                  <p className="label">Item Transfer</p>
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Nama Produk</th>
                          <th className="text-right">Jumlah</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewingTransfer.items.map((item, idx) => (
                          <tr key={item.id || idx}>
                            <td className="font-mono text-xs">{item.sku || item.product?.sku || '-'}</td>
                            <td>{item.product_name || item.product?.name || '-'}</td>
                            <td className="text-right font-medium">{formatNumber(item.quantity)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="flex justify-end pt-2">
                <button onClick={() => setViewingTransfer(null)} className="btn-secondary">Tutup</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-3xl">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Transfer Stok Baru</h3>
            </div>
            <TransferForm
              warehouses={warehouses}
              onSubmit={handleCreate}
              onClose={() => setShowCreateModal(false)}
              saving={saving}
            />
          </div>
        </div>
      )}
    </div>
  );
}
